// src/pages/Accounting/Reconcile/Session/MathEquation.tsx

import type React from "react";
import { AlertTriangle, Check } from "lucide-react";
import { PALETTE } from "../constants";
import type { Currency } from "../types";
import { formatCurrency } from "../utils";

interface MathEquationProps {
  currency: Currency;
  statementEndingBalance: number;
  clearedTotal: number;
  beginningBalance: number;
  paymentsTotal: number;
  paymentsCount: number;
  depositsTotal: number;
  depositsCount: number;
  difference: number;
  isBalanced: boolean;
}

export function MathEquation({
  currency,
  statementEndingBalance,
  clearedTotal,
  beginningBalance,
  paymentsTotal,
  paymentsCount,
  depositsTotal,
  depositsCount,
  difference,
  isBalanced,
}: MathEquationProps) {
  const diffColor = isBalanced ? PALETTE.tealDark : PALETTE.amberInk;
  const diffBg = isBalanced ? "#DCFCE7" : "#FEF3C7";

  return (
    <div style={wrapStyle}>
      <div style={leftStyle}>
        <div style={topRowStyle}>
          <div style={bigStatStyle}>
            <div style={bigNumStyle}>{formatCurrency(statementEndingBalance, currency)}</div>
            <div style={bigLabelStyle}>Statement ending</div>
          </div>
          <div style={opStyle}>&#8722;</div>
          <div style={bigStatStyle}>
            <div style={bigNumStyle}>{formatCurrency(clearedTotal, currency)}</div>
            <div style={bigLabelStyle}>Cleared in Novala</div>
          </div>
        </div>
        <div style={bottomRowStyle}>
          <div style={smallStatStyle}>
            <div style={smallNumStyle}>{formatCurrency(beginningBalance, currency)}</div>
            <div style={smallLabelStyle}>Beginning</div>
          </div>
          <div style={opSmallStyle}>&#8722;</div>
          <div style={smallStatStyle}>
            <div style={smallNumStyle}>{formatCurrency(Math.abs(paymentsTotal), currency)}</div>
            <div style={smallLabelStyle}>Payments ({paymentsCount})</div>
          </div>
          <div style={opSmallStyle}>+</div>
          <div style={smallStatStyle}>
            <div style={smallNumStyle}>{formatCurrency(depositsTotal, currency)}</div>
            <div style={smallLabelStyle}>Deposits ({depositsCount})</div>
          </div>
        </div>
      </div>

      <div style={diffWrapStyle}>
        <div style={{ ...diffIcoStyle, background: diffBg, color: diffColor }}>
          {isBalanced
            ? <Check size={20} strokeWidth={2.75} />
            : <AlertTriangle size={20} strokeWidth={2.25} />}
        </div>
        <div style={{ ...diffValStyle, color: diffColor }}>
          {isBalanced ? formatCurrency(0, currency) : formatCurrency(difference, currency)}
        </div>
        <div style={diffLblStyle}>Difference</div>
        {isBalanced && (
          <div style={balancedTipStyle}>
            <strong style={{ color: "#86EFAC" }}>Balanced.</strong> You're ready to sign off and lock the period.
          </div>
        )}
      </div>
    </div>
  );
}

const wrapStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "24px 32px", background: "#fff", borderBottom: `1px solid ${PALETTE.lineSoft}`, gap: 32 };
const leftStyle: React.CSSProperties = { flex: 1, display: "flex", flexDirection: "column", gap: 10 };
const topRowStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "center", gap: 24 };
const bigStatStyle: React.CSSProperties = { textAlign: "center", minWidth: 180 };
const bigNumStyle: React.CSSProperties = { fontSize: 26, fontWeight: 700, color: PALETTE.ink, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em", lineHeight: 1.1 };
const bigLabelStyle: React.CSSProperties = { fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: PALETTE.ink3, fontWeight: 700, marginTop: 6 };
const opStyle: React.CSSProperties = { fontSize: 22, color: PALETTE.ink3 };
const bottomRowStyle: React.CSSProperties = { display: "flex", justifyContent: "center", alignItems: "center", gap: 18, paddingTop: 12, borderTop: `1px dashed ${PALETTE.line}` };
const smallStatStyle: React.CSSProperties = { textAlign: "center" };
const smallNumStyle: React.CSSProperties = { fontSize: 16, fontWeight: 700, color: PALETTE.ink2, fontVariantNumeric: "tabular-nums" };
const smallLabelStyle: React.CSSProperties = { fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: PALETTE.ink3, fontWeight: 700, marginTop: 4 };
const opSmallStyle: React.CSSProperties = { fontSize: 16, color: PALETTE.ink3 };
const diffWrapStyle: React.CSSProperties = { paddingLeft: 32, borderLeft: `1px solid ${PALETTE.line}`, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 };
const diffIcoStyle: React.CSSProperties = { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", marginBottom: 4 };
const diffValStyle: React.CSSProperties = { fontSize: 28, fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em", lineHeight: 1 };
const diffLblStyle: React.CSSProperties = { fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: PALETTE.ink3, fontWeight: 700, marginTop: 6 };
const balancedTipStyle: React.CSSProperties = { background: PALETTE.ink, color: "#fff", padding: "10px 14px", borderRadius: 8, fontSize: 12.5, maxWidth: 240, marginTop: 10, fontWeight: 500 };